import type { Database } from "../../../../__definitions__/database-definitions";
import type { Condition } from "../manager-v2/types";

export type BooleanOperator = "IS TRUE" | "IS FALSE";

export type DateOperator =
  | "IS"
  | "BEFORE"
  | "AFTER"
  | "ON OR BEFORE"
  | "ON OR AFTER"
  | "ON"
  | "IS BLANK"
  | "IS NOT BLANK"
  | "IS FUTURE"
  | "IS FUTURE OR TODAY"
  | "IS PAST"
  | "IS PAST OR TODAY";

export type TextOperator = "IS" | "IS NOT" | "IS BLANK" | "IS NOT BLANK";

export type Operator = TextOperator | DateOperator | BooleanOperator;

type SegmentConditionDefaultOperator = "IS" | "BEFORE" | "AFTER" | "ON" | "IS EMPTY" | "IS TRUE";

export const filterFieldOperators: Record<
  Database["public"]["Tables"]["account_person_field"]["Row"]["field_type"],
  {
    operators: {
      operator: Operator;
      label: () => string;
    }[];
    defaultOperator: SegmentConditionDefaultOperator;
  }
> = {
  TEXT: {
    operators: [
      { operator: "IS" as const, label: () => "Is" },
      { operator: "IS NOT" as const, label: () => "Is Not" },
      { operator: "IS BLANK" as const, label: () => "Is Blank" },
      { operator: "IS NOT BLANK" as const, label: () => "Is Not Blank" },
    ],
    defaultOperator: "IS" as const,
  },
  MONTH_DAY: {
    operators: [
      { operator: "BEFORE" as const, label: () => "Before" },
      { operator: "AFTER" as const, label: () => "After" },
      { operator: "ON" as const, label: () => "On" },
      { operator: "IS BLANK" as const, label: () => "Is Blank" },
      { operator: "IS NOT BLANK" as const, label: () => "Is Not Blank" },
    ],
    defaultOperator: "ON" as const,
  },
  DATE: {
    operators: [
      { operator: "BEFORE" as const, label: () => "Before" },
      { operator: "AFTER" as const, label: () => "After" },
      { operator: "ON OR BEFORE" as const, label: () => "On or Before" },
      { operator: "ON OR AFTER" as const, label: () => "On or After" },
      { operator: "ON" as const, label: () => "On" },
      { operator: "IS BLANK" as const, label: () => "Is Blank" },
      { operator: "IS NOT BLANK" as const, label: () => "Is Not Blank" },
      { operator: "IS FUTURE" as const, label: () => "Is in Future" },
      { operator: "IS PAST" as const, label: () => "Is in Past" },
      { operator: "IS FUTURE OR TODAY" as const, label: () => "Is in Future or Today" },
      { operator: "IS PAST OR TODAY" as const, label: () => "Is in Past or Today" },
    ],
    defaultOperator: "ON" as const,
  },
  ADDRESS: {
    operators: [],
    defaultOperator: "IS" as const,
  },
  COUNTRY: {
    operators: [
      { operator: "IS" as const, label: () => "Is" },
      { operator: "IS NOT" as const, label: () => "Is Not" },
      { operator: "IS BLANK" as const, label: () => "Is Blank" },
      { operator: "IS NOT BLANK" as const, label: () => "Is Not Blank" },
    ],
    defaultOperator: "IS" as const,
  },
  BOOLEAN: {
    operators: [
      { operator: "IS TRUE" as const, label: () => "Is True" },
      { operator: "IS FALSE" as const, label: () => "Is False" },
    ],
    defaultOperator: "IS TRUE" as const,
  },
} as const;

export const mapBooleanOperator = (condition: Pick<Condition, "values">): Operator => {
  switch (condition.values[0]) {
    case "true":
      return "IS TRUE";
    case "false":
      return "IS FALSE";
    default:
      return "IS TRUE";
  }
};

export const mapDateOperator = (
  condition: Pick<Condition, "operation" | "invert" | "values">,
): Operator => {
  switch (condition.operation) {
    case "IS EMPTY":
      return condition.invert ? "IS NOT BLANK" : "IS BLANK";
    case "BEFORE": {
      if (condition.invert) {
        return condition.values[0] === "TODAY" ? "IS FUTURE OR TODAY" : "ON OR AFTER";
      } else {
        return condition.values[0] === "TODAY" ? "IS PAST" : "BEFORE";
      }
    }
    case "AFTER": {
      if (condition.invert) {
        return condition.values[0] === "TODAY" ? "IS PAST OR TODAY" : "ON OR BEFORE";
      } else {
        return condition.values[0] === "TODAY" ? "IS FUTURE" : "AFTER";
      }
    }
    default:
      return condition.operation;
  }
};

export const mapOperator = (condition: Pick<Condition, "operation" | "invert">): Operator => {
  const empty = condition.operation === "IS EMPTY";
  if (condition.invert) {
    return `IS NOT${empty ? " BLANK" : ""}`;
  }

  if (!condition.invert && empty) {
    return "IS BLANK";
  }

  return "IS";
};
